import React from "react";
import { useNavigate } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { faComments } from "@fortawesome/free-regular-svg-icons";
import EmptyState from "../../pages/EmptyState";
import ListSkeleton from "../../Skeleton/ListSkeleton";
import useDelayedFlag from "../../Skeleton/useDelayedFlag";
import ChatListRow from "../ChatListRow";
import useChatList from "../useChatList";
import { markThreadSeen } from "../../../utils/unreadBadges";
import { toDate } from "../../../utils/timeFormat";

// Swipe-left-to-delete wrapper. Pre-existing behaviour, kept intact — it is the
// only way to delete a chat on mobile.
function SwipeableRow({ chat, unread, onOpen, onDelete }) {
  const [style, setStyle] = React.useState({ left: 0 });
  const [armed, setArmed] = React.useState(false);

  const handlers = useSwipeable({
    onSwiping: (e) => {
      if (e.deltaX < 0) {
        setStyle({ left: Math.max(e.deltaX, -100) });
        setArmed(true);
      }
    },
    onSwiped: async (e) => {
      if (e.deltaX < -50 && armed) await onDelete();
      setArmed(false);
      setStyle({ left: 0 });
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  return (
    <div className="relative overflow-hidden">
      <div
        className={`absolute inset-y-0 right-0 flex items-center bg-red-500 ${
          armed ? "w-24" : "w-0"
        }`}
      >
        <FontAwesomeIcon className="ml-8 text-lg text-white" icon={faTrash} />
      </div>
      <div {...handlers} className="relative bg-white" style={style}>
        <ChatListRow chat={chat} unread={unread} onOpen={onOpen} />
      </div>
    </div>
  );
}

// Mobile (<1024) Chats Home. Full width, no card. Tapping a row pushes to the
// thread screen.
export default function ChatsMobileList() {
  const navigate = useNavigate();
  const { threads, loading, isUnread, removeThread, refreshUnread } =
    useChatList();
  const showListSkeleton = useDelayedFlag(loading);

  // Rows, not a full-screen spinner. The top bar and bottom nav are outside this
  // component and already on screen.
  if (loading)
    return showListSkeleton ? (
      <ListSkeleton rows={7} avatarSize={44} columns={0} rowClassName="px-4 py-3.5" />
    ) : null;

  if (threads.length === 0) {
    return (
      <EmptyState
        icon={faComments}
        title="No messages yet"
        body={
          <>
            When someone sends you a DM,
            <br />
            it'll show up here.
          </>
        }
      />
    );
  }

  const open = (chat) => {
    if (chat?.friend_details?.user_name === "deleted_user") return;
    // Watermark from the row's own last-message timestamp — see markThreadSeen.
    markThreadSeen(
      chat.id,
      toDate(chat?.last_message?.created_at)?.getTime() || Date.now()
    );
    refreshUnread();
    localStorage.setItem("id", chat.friend_details.id);
    localStorage.setItem("gid", chat.id);
    localStorage.setItem("image", chat.friend_details.image);
    navigate(`/chatHistory/${chat.friend_details.id}/${chat.id}`);
  };

  return (
    <div>
      {threads.map((chat, i) => (
        <React.Fragment key={chat.id}>
          {/* Dividers inset to the handle edge (avatar 40 + gap 12 + px 16);
              none below the last row, regardless of row count. */}
          {i > 0 && <div className="ml-[68px] border-t border-divider" />}
          <SwipeableRow
            chat={chat}
            unread={isUnread(chat)}
            onOpen={() => open(chat)}
            onDelete={async () => {
              try {
                const message = await removeThread(chat);
                if (message) toast.success(message);
              } catch (error) {
                toast.error("Could not delete the conversation. Try again.");
              }
            }}
          />
        </React.Fragment>
      ))}
    </div>
  );
}
